import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import type { CreateAppPayload } from "@/features/app-studio/model/appForm";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type { CreateAppPayload } from "@/features/app-studio/model/appForm";

export type AppStatus = "draft" | "published";

export type AppCategorySummary = {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
};

export type AppSummary = {
  id: number;
  name: string;
  description: string | null;
  domain_id: number;
  category_id: number;
  status: AppStatus;
  created_at: string;
  updated_at: string;
  is_favorite?: boolean;
};

export type UpdateAppPayload = {
  name?: string;
  description?: string | null;
  status?: AppStatus;
};

export type AppsState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  apps: AppSummary[];
  categories: AppCategorySummary[];
  detailStatus: "idle" | "loading" | "succeeded" | "failed";
  detailApp: AppSummary | null;
  detailError: string | null;
  updateStatus: "idle" | "loading" | "succeeded" | "failed";
};

export const appsInitialState: AppsState = {
  status: STATUS.IDLE,
  apps: [],
  categories: [],
  detailStatus: STATUS.IDLE,
  detailApp: null,
  detailError: null,
  updateStatus: STATUS.IDLE,
};

export function isAppCategorySummary(value: unknown): value is AppCategorySummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.name === "string" &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string"
  );
}

export function isAppSummary(value: unknown): value is AppSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.name === "string" &&
    (typeof row.description === "string" || row.description === null) &&
    typeof row.domain_id === "number" &&
    typeof row.category_id === "number" &&
    (row.status === "draft" || row.status === "published") &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string" &&
    (row.is_favorite === undefined || typeof row.is_favorite === "boolean")
  );
}

export function parseAppCategoriesResponse(data: unknown): AppCategorySummary[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  if (!data.every(isAppCategorySummary)) {
    return null;
  }
  return data;
}

export function parseAppsResponse(data: unknown): AppSummary[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  if (!data.every(isAppSummary)) {
    return null;
  }
  return data;
}

export const createApp = createAsyncThunk<
  AppSummary,
  CreateAppPayload,
  { rejectValue: string }
>("apps/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/apps", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isAppSummary(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const fetchApps = createAsyncThunk<AppSummary[], void, { rejectValue: string }>(
  "apps/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiFetch("/v1/apps", { method: "GET" });
      if (!res.ok) {
        return rejectWithValue(`Request failed (${res.status})`);
      }
      const data: unknown = await res.json();
      const apps = parseAppsResponse(data);
      if (!apps) {
        return rejectWithValue("Unexpected response shape");
      }
      return apps;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

export const fetchAppById = createAsyncThunk<
  AppSummary,
  number,
  { rejectValue: string }
>("apps/fetchById", async (id, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/apps/${id}`, { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isAppSummary(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

export type UpdateAppArgs = {
  id: number;
  payload: UpdateAppPayload;
};

export const updateApp = createAsyncThunk<
  { id: number; payload: UpdateAppPayload },
  UpdateAppArgs,
  { rejectValue: string }
>("apps/update", async ({ id, payload }, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/apps/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return { id, payload };
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const deleteApp = createAsyncThunk<number, number, { rejectValue: string }>(
  "apps/delete",
  async (appId, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/v1/apps/${appId}`, { method: "DELETE" });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      return appId;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

export const fetchAppCategories = createAsyncThunk<
  AppCategorySummary[],
  void,
  { rejectValue: string }
>("apps/fetchCategories", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/app-categories", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    const categories = parseAppCategoriesResponse(data);
    if (!categories) {
      return rejectWithValue("Unexpected response shape");
    }
    return categories;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

const appsSlice = createSlice({
  name: "apps",
  initialState: appsInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchApps.pending, (state) => {
        state.status = STATUS.LOADING;
      })
      .addCase(fetchApps.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.apps = action.payload;
      })
      .addCase(fetchApps.rejected, (state) => {
        state.status = STATUS.SUCCEEDED;
        state.apps = [];
      })
      .addCase(fetchAppCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(createApp.fulfilled, (state, action) => {
        state.apps = [action.payload, ...state.apps];
      })
      .addCase(fetchAppById.pending, (state) => {
        state.detailStatus = STATUS.LOADING;
        state.detailError = null;
      })
      .addCase(fetchAppById.fulfilled, (state, action) => {
        state.detailStatus = STATUS.SUCCEEDED;
        state.detailApp = action.payload;
        const index = state.apps.findIndex((app) => app.id === action.payload.id);
        if (index >= 0) {
          state.apps[index] = action.payload;
        }
      })
      .addCase(fetchAppById.rejected, (state, action) => {
        state.detailStatus = STATUS.FAILED;
        state.detailApp = null;
        state.detailError = action.payload ?? "Request failed";
      })
      .addCase(updateApp.pending, (state) => {
        state.updateStatus = STATUS.LOADING;
      })
      .addCase(updateApp.fulfilled, (state, action) => {
        state.updateStatus = STATUS.SUCCEEDED;
        const { id, payload } = action.payload;
        const applyUpdate = (app: AppSummary) => {
          if ("name" in payload && payload.name !== undefined) {
            app.name = payload.name;
          }
          if ("description" in payload) {
            app.description = payload.description ?? null;
          }
          if ("status" in payload && payload.status !== undefined) {
            app.status = payload.status;
          }
        };
        if (state.detailApp?.id === id) {
          applyUpdate(state.detailApp);
        }
        const listIndex = state.apps.findIndex((app) => app.id === id);
        if (listIndex >= 0) {
          applyUpdate(state.apps[listIndex]);
        }
      })
      .addCase(updateApp.rejected, (state) => {
        state.updateStatus = STATUS.FAILED;
      })
      .addCase(deleteApp.fulfilled, (state, action) => {
        state.apps = state.apps.filter((app) => app.id !== action.payload);
        if (state.detailApp?.id === action.payload) {
          state.detailApp = null;
          state.detailStatus = STATUS.IDLE;
          state.detailError = null;
        }
      });
  },
});

export const appsReducer = appsSlice.reducer;
